/**
 * Client API axios avec intercepteurs JWT.
 * Gère automatiquement le refresh des tokens expirés.
 */
import axios from "axios";

// VITE_API_BASE_URL = host de l'API (ex: https://mon-ministere.up.railway.app)
// /api/v1 est ajouté côté code → la valeur env doit être le host seul.
// Fallback dev : localhost:8080
const API_BASE = import.meta.env.VITE_API_BASE_URL || "http://localhost:8080";
const API_URL = `${API_BASE}/api/v1`;

// ─── Toast handler injecté par ToastProvider (au mount) pour notifications globales ───
let _toast = null;
export const registerToastHandler = (handler) => { _toast = handler; };

// ─── Storage helpers ───
export const tokenStorage = {
  getAccess: () => localStorage.getItem("mm_access_token"),
  getRefresh: () => localStorage.getItem("mm_refresh_token"),
  getUser: () => {
    const raw = localStorage.getItem("mm_user");
    try {
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  },
  set: ({ access_token, refresh_token, user }) => {
    if (access_token) localStorage.setItem("mm_access_token", access_token);
    if (refresh_token) localStorage.setItem("mm_refresh_token", refresh_token);
    if (user) localStorage.setItem("mm_user", JSON.stringify(user));
  },
  clear: () => {
    localStorage.removeItem("mm_access_token");
    localStorage.removeItem("mm_refresh_token");
    localStorage.removeItem("mm_user");
  },
};

// ─── Instance axios ───
const api = axios.create({
  baseURL: API_URL,
  timeout: 15000,
  headers: { "Content-Type": "application/json" },
});

// ─── Intercepteur de requête : injecte le token ───
api.interceptors.request.use(
  (config) => {
    const token = tokenStorage.getAccess();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ─── Intercepteur de réponse : gère le refresh automatique ───
let isRefreshing = false;
let pendingQueue = [];

const processQueue = (error, token = null) => {
  pendingQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  pendingQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes("/auth/")
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          pendingQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = tokenStorage.getRefresh();
      if (!refreshToken) {
        tokenStorage.clear();
        window.location.href = "/login";
        return Promise.reject(error);
      }

      try {
        const { data } = await axios.post(`${API_URL}/auth/refresh`, {
          refresh_token: refreshToken,
        });
        tokenStorage.set({
          access_token: data.access_token,
          refresh_token: data.refresh_token,
        });
        api.defaults.headers.Authorization = `Bearer ${data.access_token}`;
        processQueue(null, data.access_token);
        originalRequest.headers.Authorization = `Bearer ${data.access_token}`;
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        tokenStorage.clear();
        window.location.href = "/login";
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // ─── Toasts globaux pour erreurs récurrentes (403/422/réseau) ───
    // Skip pour /auth/* : la page Login gère ses erreurs localement.
    const url = originalRequest?.url || "";
    const isAuthEndpoint = url.includes("/auth/");
    const status = error.response?.status;

    if (_toast && !isAuthEndpoint) {
      if (status === 403) {
        _toast("Accès non autorisé pour cette action", "error");
      } else if (status === 422) {
        const detail = error.response?.data?.detail;
        let msg = "Données invalides";
        if (Array.isArray(detail) && detail[0]?.msg) {
          msg = detail[0].msg.replace(/^Value error,\s*/, "");
        } else if (typeof detail === "string") {
          msg = detail;
        }
        _toast(msg, "error");
      } else if (!error.response && error.code !== "ERR_CANCELED") {
        // Pas de réponse = backend down / réseau coupé
        _toast("Backend indisponible — vérifiez la connexion", "warning");
      }
    }

    return Promise.reject(error);
  }
);

// ─── Endpoints groupés ───
export const authApi = {
  login: (email, password) => api.post("/auth/login", { email, password }),
  register: (data) => api.post("/auth/register", data),
  registerFidele: (data) => api.post("/auth/register-fidele", data),
  onboarding: (data) => api.post("/auth/onboarding", data),
  refresh: (refresh_token) => api.post("/auth/refresh", { refresh_token }),
  logout: () => api.post("/auth/logout"),
  me: () => api.get("/auth/me"),
  updateMe: (data) => api.put("/auth/me", data),
  uploadPhoto: (file, onProgress) => {
    const fd = new FormData();
    fd.append("file", file);
    return api.post("/auth/me/photo", fd, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: onProgress,
    });
  },
  removePhoto: () => api.delete("/auth/me/photo"),
  changePassword: (current_password, new_password) =>
    api.post("/auth/change-password", { current_password, new_password }),
};

export const membersApi = {
  list: (params = {}) => api.get("/members", { params }),
  get: (id) => api.get(`/members/${id}`),
  create: (data) => api.post("/members", data),
  update: (id, data) => api.patch(`/members/${id}`, data),
  remove: (id) => api.delete(`/members/${id}`),
};

export const eglisesApi = {
  list: () => api.get("/eglises"),
  mesEglises: () => api.get("/eglises/mes-eglises"),
  get: (id) => api.get(`/eglises/${id}`),
  create: (data) => api.post("/eglises", data),
  update: (id, data) => api.patch(`/eglises/${id}`, data),
  remove: (id) => api.delete(`/eglises/${id}`),
  regenererCode: (id) => api.post(`/eglises/${id}/regenerer-code`),
};

export const evenementsApi = {
  list: (params = {}) => api.get("/evenements", { params }),
  get: (id) => api.get(`/evenements/${id}`),
  create: (data) => api.post("/evenements", data),
  update: (id, data) => api.patch(`/evenements/${id}`, data),
  remove: (id) => api.delete(`/evenements/${id}`),
};

export const presencesApi = {
  signalerAbsences: (data) => api.post("/presences/absences", data),
  marquerCellule:   (data) => api.post("/presences/marquage", data),
  absentsToday:     (params = {}) => api.get("/presences/absences/today", { params }),
  membreStats:      (id, months = 3) => api.get(`/presences/membre/${id}/stats`, { params: { months } }),
  alertes:          (params = {}) => api.get("/presences/alertes", { params }),
};

export const cellulesApi = {
  list:         (params = {}) => api.get("/cellules", { params }),
  get:          (id) => api.get(`/cellules/${id}`),
  create:       (data) => api.post("/cellules", data),
  update:       (id, data) => api.put(`/cellules/${id}`, data),
  remove:       (id) => api.delete(`/cellules/${id}`),
  membres:      (id) => api.get(`/cellules/${id}/membres`),
  addMembre:    (id, memberId) => api.post(`/cellules/${id}/membres/${memberId}`),
  removeMembre: (id, memberId) => api.delete(`/cellules/${id}/membres/${memberId}`),
};

export const donsApi = {
  list:   (params = {}) => api.get("/dons", { params }),
  stats:  (params = {}) => api.get("/dons/stats", { params }),
  create: (data) => api.post("/dons", data),
  update: (id, data) => api.patch(`/dons/${id}`, data),
  remove: (id) => api.delete(`/dons/${id}`),
};

export const benevolesApi = {
  // Équipes
  listEquipes:   (params = {}) => api.get("/benevoles/equipes", { params }),
  createEquipe:  (data) => api.post("/benevoles/equipes", data),
  updateEquipe:  (id, data) => api.put(`/benevoles/equipes/${id}`, data),
  removeEquipe:  (id) => api.delete(`/benevoles/equipes/${id}`),
  // Planning
  listPlanning:        (params = {}) => api.get("/benevoles/planning", { params }),
  createPlanning:      (data) => api.post("/benevoles/planning", data),
  createPlanningBatch: (data) => api.post("/benevoles/planning/batch", data),
  removePlanning:      (id) => api.delete(`/benevoles/planning/${id}`),
  repondre:            (id, data) => api.put(`/benevoles/planning/${id}/repondre`, data),
  planningEvenement:   (eventId) => api.get(`/benevoles/planning/evenement/${eventId}`),
  summaryEvenement:    (eventId) => api.get(`/benevoles/planning/evenement/${eventId}/summary`),
  stats:               (params = {}) => api.get("/benevoles/planning/stats", { params }),
};

export const quetesApi = {
  list:          (params = {}) => api.get("/quetes", { params }),
  stats:         (params = {}) => api.get("/quetes/stats", { params }),
  get:           (id) => api.get(`/quetes/${id}`),
  byEvenement:   (evenementId) => api.get(`/quetes/by-evenement/${evenementId}`),
  create:        (data) => api.post("/quetes", data),
  update:        (id, data) => api.put(`/quetes/${id}`, data),
  remove:        (id) => api.delete(`/quetes/${id}`),
};

export const dashboardApi = {
  get: () => api.get("/dashboard"),
};

export const importsApi = {
  /** POST multipart/form-data : analyse le fichier sans rien sauvegarder. */
  preview: (file, onProgress) => {
    const fd = new FormData();
    fd.append("file", file);
    return api.post("/imports/membres/preview", fd, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: onProgress,
    });
  },
  /** POST JSON : importe les lignes validées en base. */
  confirm: (data) => api.post("/imports/membres/confirm", data),
  /** Télécharge le modèle Excel généré. */
  template: () => api.get("/imports/membres/template", { responseType: "blob" }),
};

/** Helper : déclenche un download navigateur depuis une réponse blob axios. */
function triggerDownload(response, fallbackName) {
  const cd = response.headers["content-disposition"] || "";
  const match = cd.match(/filename="?([^"]+)"?/i);
  const filename = match ? match[1] : fallbackName;
  const url = URL.createObjectURL(new Blob([response.data], { type: response.headers["content-type"] }));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
  return filename;
}

export const exportsApi = {
  csv:   (params = {}) => api.get("/exports/membres/csv",   { params, responseType: "blob" }),
  excel: (params = {}) => api.get("/exports/membres/excel", { params, responseType: "blob" }),
  pdf:   (params = {}) => api.get("/exports/membres/pdf",   { params, responseType: "blob" }),
  triggerDownload,
};

// ─── Validation des inscriptions fidèles (Option B) ───
export const inscriptionsApi = {
  enAttente: () => api.get("/inscriptions/en-attente"),
  stats:     () => api.get("/inscriptions/stats"),
  valider:   (userId) => api.post(`/inscriptions/${userId}/valider`),
  refuser:   (userId, raison = null) => api.post(`/inscriptions/${userId}/refuser`, { raison }),
};

// ─── Notifications in-app ───
export const notificationsApi = {
  list:      (params = {}) => api.get("/notifications", { params }),
  count:     () => api.get("/notifications/count"),
  lire:      (id) => api.put(`/notifications/${id}/lire`),
  lireTout:  () => api.put("/notifications/lire-tout"),
};

// ─── Page publique : config admin (auth) ───
export const pagePubliqueApi = {
  get:        () => api.get("/page-publique/ma-page"),
  configurer: (data) => api.post("/page-publique/configurer", data),
  publier:    (is_active) => api.put("/page-publique/publier", { is_active }),
  stream:     (data) => api.put("/page-publique/stream", data),
};

// ─── Page publique : routes ouvertes (sans auth, sans intercepteur JWT) ───
const publicAxios = axios.create({
  baseURL: API_URL,
  timeout: 15000,
  headers: { "Content-Type": "application/json" },
});

export const publicApi = {
  eglise:     (slug) => publicAxios.get(`/public/${slug}`),
  evenements: (slug) => publicAxios.get(`/public/${slug}/evenements`),
  annonces:   (slug) => publicAxios.get(`/public/${slug}/annonces`),
  stream:     (slug) => publicAxios.get(`/public/${slug}/stream`),
  don:        (slug, data) => publicAxios.post(`/public/${slug}/don`, data),
  contact:    (slug, data) => publicAxios.post(`/public/${slug}/contact`, data),
};

export default api;
